/**
 * UserManagement — kelola admin users (super_admin only)
 *
 * Fitur:
 *  - List semua user dengan role & status badge
 *  - Tambah admin baru (username + password + role)
 *  - Nonaktifkan / Aktifkan akun (toggle)
 *  - Hapus akun
 *  - Edit password / role
 *  - Ganti password akun sendiri
 */
import React, { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import { AnimatePresence, motion } from "framer-motion";
import AdminPageHeader from "../_shared/AdminPageHeader";
import AdminCard from "../_shared/AdminCard";
import {
  fetchUsers, createUser, updateUser, deleteUser, changePassword,
} from "../../../api/usersAPI";

// ── Helpers ───────────────────────────────────────────────────
function decodeToken() {
  try {
    const t = localStorage.getItem("accessToken");
    if (!t) return null;
    return JSON.parse(atob(t.split(".")[1]));
  } catch { return null; }
}

function RoleBadge({ role }) {
  if (role === "super_admin")
    return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-200">👑 Super Admin</span>;
  return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brand-100 text-brand-700 border border-brand-200">🔑 Admin</span>;
}

function StatusBadge({ status, isActive }) {
  if (!isActive || status === "inactive")
    return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-600 border border-red-200">⛔ Nonaktif</span>;
  return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200">✅ Aktif</span>;
}

// ── Modal Tambah / Edit Admin ─────────────────────────────────
function UserModal({ user, onClose, onSaved }) {
  const isEdit = Boolean(user);
  const [form, setForm] = useState({
    username: user?.username || "",
    role:     user?.role     || "admin",
    password: "",
    confirm:  "",
  });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const set = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isEdit && !form.username) return setError("Username wajib diisi.");
    if (!isEdit && !form.password) return setError("Password wajib diisi.");
    if (form.password && form.password.length < 8) return setError("Password minimal 8 karakter.");
    if (form.password && form.password !== form.confirm) return setError("Konfirmasi password tidak cocok.");

    setLoading(true);
    try {
      if (isEdit) {
        const fields = {};
        if (form.username !== user.username) fields.username = form.username;
        if (form.role     !== user.role)     fields.role     = form.role;
        if (form.password)                   fields.password = form.password;
        await updateUser(user.id, fields);
      } else {
        await createUser({ username: form.username, password: form.password, role: form.role });
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 16 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="font-oswald text-lg font-semibold text-slate-900">
            {isEdit ? `Edit: ${user.username}` : "Tambah Admin Baru"}
          </h3>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 transition-all">✕</button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          {/* Username */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 font-overpass">
              Username {!isEdit && <span className="text-red-500">*</span>}
            </label>
            <input name="username" type="text" value={form.username} onChange={set}
              placeholder="username" autoComplete="off"
              className="w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none font-overpass text-sm text-slate-800 placeholder:text-slate-400 transition-all" />
          </div>

          {/* Role */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 font-overpass">Role</label>
            <select name="role" value={form.role} onChange={set}
              className="w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none font-overpass text-sm text-slate-800 transition-all">
              <option value="admin">Admin — kelola konten</option>
              <option value="super_admin">Super Admin — akses penuh + kelola user</option>
            </select>
          </div>

          {/* Password */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 font-overpass">
              {isEdit ? "Password Baru (kosongkan jika tidak diubah)" : "Password *"}
            </label>
            <div className="relative">
              <input name="password" type={showPw ? "text" : "password"} value={form.password} onChange={set}
                placeholder={isEdit ? "Isi untuk ganti password" : "Minimal 8 karakter"}
                autoComplete="new-password"
                className="w-full px-3 pr-10 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none font-overpass text-sm text-slate-800 placeholder:text-slate-400 transition-all" />
              <button type="button" onClick={() => setShowPw(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d={showPw
                      ? "M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                      : "M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"} />
                </svg>
              </button>
            </div>
          </div>

          {/* Confirm */}
          {form.password && (
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 font-overpass">Konfirmasi Password *</label>
              <input name="confirm" type={showPw ? "text" : "password"} value={form.confirm} onChange={set}
                placeholder="Ulangi password" autoComplete="new-password"
                className="w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none font-overpass text-sm text-slate-800 placeholder:text-slate-400 transition-all" />
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm font-overpass">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-all">
              Batal
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-brand-600 to-eco-600 text-white text-sm font-semibold shadow-lg shadow-brand-500/20 hover:-translate-y-0.5 transition-all disabled:opacity-60">
              {loading ? "Menyimpan..." : isEdit ? "💾 Update" : "✨ Tambah Admin"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// ── Modal Ganti Password Sendiri ─────────────────────────────
function ChangePwModal({ onClose }) {
  const [form, setForm]   = useState({ old: "", newPw: "", confirm: "" });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState("");

  const set = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.old || !form.newPw) return setError("Semua field wajib diisi.");
    if (form.newPw.length < 8) return setError("Password baru minimal 8 karakter.");
    if (form.newPw !== form.confirm) return setError("Konfirmasi tidak cocok.");
    setLoading(true);
    try {
      await changePassword({ oldPassword: form.old, newPassword: form.newPw });
      await Swal.fire({ icon: "success", title: "Password diubah!", timer: 1500, showConfirmButton: false });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="font-oswald text-lg font-semibold text-slate-900">🔐 Ganti Password Saya</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 transition-all">✕</button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {[
            { name: "old",     label: "Password Lama",              ph: "••••••••" },
            { name: "newPw",   label: "Password Baru",              ph: "Minimal 8 karakter" },
            { name: "confirm", label: "Konfirmasi Password Baru",   ph: "Ulangi password baru" },
          ].map(({ name, label, ph }) => (
            <div key={name}>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5 font-overpass">{label}</label>
              <div className="relative">
                <input name={name} type={showPw ? "text" : "password"} value={form[name]} onChange={set}
                  placeholder={ph}
                  className="w-full px-3 pr-10 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand-400 focus:ring-2 focus:ring-brand-100 outline-none font-overpass text-sm text-slate-800 placeholder:text-slate-400 transition-all" />
                {name === "old" && (
                  <button type="button" onClick={() => setShowPw(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d={showPw ? "M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                          : "M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"} />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}
          {error && (
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm font-overpass">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          )}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-all">Batal</button>
            <button type="submit" disabled={loading} className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-brand-600 to-eco-600 text-white text-sm font-semibold shadow-lg shadow-brand-500/20 hover:-translate-y-0.5 transition-all disabled:opacity-60">
              {loading ? "Menyimpan..." : "Simpan"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────
const UserManagement = () => {
  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [modalUser, setModalUser]   = useState(undefined); // undefined=tutup, null=tambah, obj=edit
  const [showChangePw, setShowChangePw] = useState(false);

  const me = decodeToken();
  const isSuperAdmin = me?.role === "super_admin";

  const load = useCallback(async () => {
    setLoading(true);
    try { setUsers(await fetchUsers()); }
    catch (err) { Swal.fire({ icon: "error", title: "Gagal memuat", text: err.message }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Toggle aktif / nonaktif
  const handleToggle = async (user) => {
    const isActivating = !user.is_active || user.status === "inactive";
    const confirm = await Swal.fire({
      icon: isActivating ? "question" : "warning",
      title: isActivating ? `Aktifkan "${user.username}"?` : `Nonaktifkan "${user.username}"?`,
      text: isActivating
        ? "User ini akan bisa login kembali."
        : "User ini tidak akan bisa login.",
      showCancelButton: true,
      confirmButtonText: isActivating ? "Ya, aktifkan!" : "Ya, nonaktifkan!",
      cancelButtonText: "Batal",
      confirmButtonColor: isActivating ? "#22c55e" : "#f59e0b",
    });
    if (!confirm.isConfirmed) return;
    try {
      await updateUser(user.id, {
        status: isActivating ? "active" : "inactive",
        is_active: isActivating ? 1 : 0,
      });
      load();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Gagal", text: err.message });
    }
  };

  const handleDelete = async (user) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: `Hapus "${user.username}"?`,
      text: "User yang dihapus tidak bisa dikembalikan.",
      showCancelButton: true,
      confirmButtonText: "Ya, hapus!",
      cancelButtonText: "Batal",
      confirmButtonColor: "#ef4444",
    });
    if (!confirm.isConfirmed) return;
    try {
      Swal.fire({ title: "Menghapus...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      await deleteUser(user.id);
      Swal.fire({ icon: "success", title: "Dihapus!", timer: 1200, showConfirmButton: false });
      load();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Gagal", text: err.message });
    }
  };

  const handleSaved = () => {
    setModalUser(undefined);
    load();
    Swal.fire({ icon: "success", title: "Berhasil!", timer: 1200, showConfirmButton: false });
  };

  if (!isSuperAdmin) {
    return (
      <div className="flex-1 min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center p-10 max-w-sm">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="font-oswald text-xl font-semibold text-slate-800 mb-2">Akses Terbatas</h2>
          <p className="text-sm text-slate-500 font-overpass">Halaman ini hanya bisa diakses oleh <strong>Super Admin</strong>.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-screen bg-slate-50 overflow-y-auto">
      <div className="p-6 lg:p-10 max-w-5xl mx-auto">
        <AdminPageHeader
          title="User Management"
          subtitle="Kelola akun admin — hanya Super Admin yang bisa akses"
          badge={users.length}
          actionLabel="➕ Tambah Admin"
          onAction={() => setModalUser(null)}
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          }
        />

        <AdminCard>
          {/* Info akun sendiri + ganti password */}
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-slate-100">
            <div className="flex items-center gap-2 text-sm text-slate-600 font-overpass">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-brand-500 flex items-center justify-center text-white text-sm font-bold">
                {me?.username?.charAt(0)?.toUpperCase()}
              </div>
              Login sebagai <strong className="text-slate-800">{me?.username}</strong>
              <RoleBadge role={me?.role} />
            </div>
            <button onClick={() => setShowChangePw(true)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-600 hover:text-brand-700 hover:bg-brand-50 px-3 py-1.5 rounded-lg transition-all">
              🔐 Ganti Password Saya
            </button>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-slate-100">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    {["No", "Username", "Role", "Status", "Last Login", "Aksi"].map(h => (
                      <th key={h} className="text-left px-5 py-3 font-semibold text-slate-700 font-overpass whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, i) => {
                    const isMe = u.id === me?.userId;
                    const isActive = u.is_active && u.status !== "inactive";
                    return (
                      <tr key={u.id} className={`border-b border-slate-50 transition-colors ${isActive ? "hover:bg-slate-50/70" : "bg-red-50/30 hover:bg-red-50/50"}`}>
                        <td className="px-5 py-4 text-slate-400 font-semibold">{i + 1}</td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2.5">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${u.role === "super_admin" ? "bg-gradient-to-br from-purple-500 to-brand-500" : "bg-gradient-to-br from-brand-500 to-eco-500"}`}>
                              {u.username.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <span className="font-semibold text-slate-800">{u.username}</span>
                              {isMe && <span className="ml-1.5 text-xs text-brand-500 font-overpass">(kamu)</span>}
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4"><RoleBadge role={u.role} /></td>
                        <td className="px-5 py-4"><StatusBadge status={u.status} isActive={u.is_active} /></td>
                        <td className="px-5 py-4 text-slate-500 font-overpass text-xs whitespace-nowrap">
                          {u.last_login
                            ? new Date(u.last_login).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" })
                            : <span className="text-slate-300 italic">Belum pernah</span>}
                        </td>
                        <td className="px-5 py-4">
                          {isMe ? (
                            <span className="text-xs text-slate-400 italic font-overpass">akun aktif</span>
                          ) : (
                            <div className="flex items-center gap-2 flex-wrap">
                              {/* Edit */}
                              <button onClick={() => setModalUser(u)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-brand-50 text-brand-700 text-xs font-semibold hover:bg-brand-100 transition-all">
                                ✏️ Edit
                              </button>
                              {/* Toggle aktif/nonaktif */}
                              <button onClick={() => handleToggle(u)}
                                className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${isActive
                                  ? "bg-amber-50 text-amber-700 hover:bg-amber-100"
                                  : "bg-green-50 text-green-700 hover:bg-green-100"}`}>
                                {isActive ? "⛔ Nonaktifkan" : "✅ Aktifkan"}
                              </button>
                              {/* Hapus */}
                              <button onClick={() => handleDelete(u)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-50 text-red-600 text-xs font-semibold hover:bg-red-100 transition-all">
                                🗑️ Hapus
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {!users.length && !loading && (
                    <tr>
                      <td colSpan={6} className="px-5 py-12 text-center text-slate-400 font-overpass">
                        Belum ada user. Klik <strong>"Tambah Admin"</strong> untuk mulai.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Legend */}
          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            <div className="flex items-start gap-3 p-4 rounded-xl bg-purple-50 border border-purple-100">
              <span className="text-xl">👑</span>
              <div>
                <p className="font-semibold text-purple-800 text-sm">Super Admin</p>
                <p className="text-xs text-purple-600 font-overpass mt-0.5">
                  Dibuat via <code className="bg-purple-100 px-1 rounded">node create-superadmin.js</code> di server. Akses penuh termasuk kelola user.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 rounded-xl bg-brand-50 border border-brand-100">
              <span className="text-xl">🔑</span>
              <div>
                <p className="font-semibold text-brand-800 text-sm">Admin</p>
                <p className="text-xs text-brand-600 font-overpass mt-0.5">
                  Dibuat oleh Super Admin di sini. Bisa kelola konten (blog, project, service, client).
                </p>
              </div>
            </div>
          </div>
        </AdminCard>
      </div>

      <AnimatePresence>
        {modalUser !== undefined && (
          <UserModal user={modalUser} onClose={() => setModalUser(undefined)} onSaved={handleSaved} />
        )}
        {showChangePw && (
          <ChangePwModal onClose={() => setShowChangePw(false)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserManagement;
